import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Search, Users, ClipboardList, Receipt, Stethoscope } from "lucide-react";

// Chairside home: find a patient and open their chart in as few taps as
// possible. Everything else in the clinic (queue, billing, certificates) is the
// shared machinery, so this links out rather than reimplementing it.
export default function DentalDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [patients, setPatients] = useState([]);
  const [queue, setQueue] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const hospital = user?.active_hospital_name || user?.hospital_name || "this clinic";

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get("/patients").catch(() => ({ data: [] })),
      api.get("/queue/today").catch(() => ({ data: [] })),
    ]).then(([p, qd]) => {
      if (cancelled) return;
      setPatients(Array.isArray(p.data) ? p.data : []);
      setQueue(Array.isArray(qd.data) ? qd.data : []);
      setLoading(false);
    }).catch((e) => {
      if (!cancelled) { toast.error(formatErr(e?.response?.data?.detail)); setLoading(false); }
    });
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const list = term
      ? patients.filter((p) =>
          (p.full_name || "").toLowerCase().includes(term) ||
          (p.contact_number || "").includes(term))
      : patients;
    return list.slice(0, 40);
  }, [patients, q]);

  const inChair = queue.filter((x) => x.status === "in_progress");
  const waiting = queue.filter((x) => x.status === "waiting");

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="dental-dashboard">
      <div>
        <h1 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Work Sans" }}>
          Dental
        </h1>
        <p className="text-sm text-slate-500">{hospital}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="In chair" value={inChair.length} tone="bg-blue-50 text-blue-700" />
        <Stat label="Waiting" value={waiting.length} tone="bg-amber-50 text-amber-700" />
        <Stat label="Patients" value={patients.length} tone="bg-slate-50 text-slate-700" />
        <Card className="p-4 flex items-center justify-center">
          <Button asChild size="sm" variant="outline" className="w-full">
            <Link to="/reception"><ClipboardList size={14} className="mr-1" /> Queue</Link>
          </Button>
        </Card>
      </div>

      {inChair.length ? (
        <Card className="p-4" data-testid="dental-in-chair">
          <h2 className="font-semibold text-slate-900 mb-2">In chair now</h2>
          <div className="space-y-2">
            {inChair.map((x) => (
              <div key={x.id} className="flex items-center justify-between gap-2 p-2 rounded-md bg-blue-50">
                <div className="min-w-0">
                  <div className="font-medium text-slate-900 truncate">
                    {x.patient_snapshot?.full_name || "Patient"}
                  </div>
                  <div className="text-xs text-slate-500 truncate">{x.reason || "—"}</div>
                </div>
                {x.patient_id ? (
                  <Button size="sm" onClick={() => nav(`/dental/patients/${x.patient_id}/chart`)}
                          className="bg-blue-600 hover:bg-blue-700 shrink-0">
                    <Stethoscope size={14} className="mr-1" /> Chart
                  </Button>
                ) : null}
              </div>
            ))}
          </div>
        </Card>
      ) : null}

      <Card className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Users size={16} className="text-slate-500" />
          <h2 className="font-semibold text-slate-900">Open a chart</h2>
        </div>
        <div className="relative mb-3">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            className="pl-9"
            placeholder="Search patient by name or contact number"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="dental-patient-search"
          />
        </div>

        {loading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-sm text-slate-500">
            No patients match. <Link to="/patients" className="text-blue-600 hover:underline">Register a patient</Link>.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {filtered.map((p) => (
              <button
                key={p.id}
                onClick={() => nav(`/dental/patients/${p.id}/chart`)}
                className="text-left p-3 rounded-lg border border-slate-200 hover:border-blue-400 hover:bg-blue-50/40 transition"
                data-testid={`dental-patient-${p.id}`}
              >
                <div className="font-medium text-slate-900 truncate">{p.full_name}</div>
                <div className="text-xs text-slate-500 truncate">
                  {p.gender || "—"} · {p.birthday || "no birthday"}
                </div>
              </button>
            ))}
          </div>
        )}
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Shortcut to="/pos" icon={Receipt} label="Billing" hint="Charge procedures" />
        <Shortcut to="/pos/services" icon={Receipt} label="Procedure prices" hint="Edit the fee schedule" />
        <Shortcut to="/followups" icon={ClipboardList} label="Follow-ups" hint="Upcoming visits" />
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  return (
    <Card className="p-4">
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-2xl font-extrabold mt-1 inline-block px-2 rounded ${tone}`}>{value}</div>
    </Card>
  );
}

function Shortcut({ to, icon: Icon, label, hint }) {
  return (
    <Card className="p-4">
      <Icon size={18} className="text-blue-700 mb-2" />
      <div className="font-semibold text-slate-900">{label}</div>
      <div className="text-xs text-slate-500 mb-3">{hint}</div>
      <Button asChild size="sm" variant="outline" className="w-full">
        <Link to={to}>Open</Link>
      </Button>
    </Card>
  );
}
